import React, { useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { useSsoCart } from "../../../contexts/SsoCartContext";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import AddShoppingCartOutlinedIcon from "@mui/icons-material/AddShoppingCartOutlined";
import CircularProgress from "@mui/material/CircularProgress";
/*istanbul ignore next */
const ContentCard = ({ item, hideCart = false }) => {
  const { addToCart, removeFromCart } = useSsoCart();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [inCart, setInCart] = useState(item.isAddedInCart || false);

  const handleCardClick = () => {
    if (item.documentType === 2 || item.documentType === 3) {
      // For catalog and bundle, always allow navigation
      history.push(`/purchase/contents/${item.id}`);
    } else {
      // Navigate to preview page and pass preview metadata through state
      history.push({
        pathname: `/purchase/preview/${item.id}`,
        state: {
          isPreview: item.isPreview,
          previewType: item.previewType,
          marketingDescription: item.marketingDescription,
        },
      });
    }
  };

  const handleCartClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      if (inCart) {
        const result = await removeFromCart(item);
        if (result.success) {
          setInCart(false);
        } else {
          console.error("Failed to remove from cart:", result.error);
        }
      } else {
        const result = await addToCart(item);
        if (result.success) {
          setInCart(true);
        } else {
          console.error("Failed to add to cart:", result.error);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardClick();
    }
  };

  const getDocumentIcon = () => {
    // For Catalog
    if (item.documentType === 2) {
      return (
        <img
          src="/images/Catalog-1.png"
          alt="Catalog"
          style={{ width: "24px", height: "24px", marginTop: "20px" }}
        />
      );
    }

    // For Bundle
    if (item.documentType === 3) {
      return (
        <img
          src="/images/Bundle-1.png"
          alt="Bundle"
          style={{ width: "24px", height: "24px", marginTop: "20px" }}
        />
      );
    }

    // For Content (documentType === 1), show icon based on typeOfContent
    const contentTypeIcons = {
      1: "PDF-1.png", // PDF
      2: "XFDL-1.png", // FORMS_XFDL
      3: "JPG-1.png", // JPG
      4: "Word-1.png", // WORD
      5: "Excel-1.png", // EXCEL
      6: "PPT-1.png", // PPT
      7: "Audio-1.png", // AUDIO
      8: "gif 1-1.png", // GIF
      9: "PNG-1.png", // PNG
      10: "Video-1.png", // VIDEO
      11: "Weblink-1.png", // WEBLINK
    };

    const iconFileName =
      contentTypeIcons[item.typeOfContent] || "Content-1.png";
    const altText = item.typeOfContent
      ? `Type ${item.typeOfContent}`
      : "Content";

    return (
      <img
        src={`/images/${iconFileName}`}
        alt={altText}
        style={{ width: "24px", height: "24px", marginTop: "20px" }}
      />
    );
  };

  const formatPriceDisplay = () => {
    // If item has subscription options (paid content), always show full price with frequency
    // Only show "$0" without frequency for truly free content (no subscriptions)
    if (item.subscriptionOptions && item.subscriptionOptions.length > 0) {
      return item.price;
    }
    // For free content with no subscriptions
    if (item.price === "Free" || item.price === "$0") {
      return "$0";
    }
    return item.price;
  };

  const renderCartIcon = () => {
    if (loading) {
      return <CircularProgress size={20} style={{ color: "#1dcaff" }} />;
    }
    if (inCart) {
      return <AddShoppingCartOutlinedIcon style={{ color: "#c41e3a" }} />;
    }
    return <ShoppingCartOutlinedIcon style={{ color: "#1dcaff" }} />;
  };

  return (
    <button
      className="content-card"
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      type="button"
      style={{ cursor: "pointer" }}
    >
      <div className="left">
        <div>{getDocumentIcon()}</div>
        <div>
          <div className="title" style={{ color: "#333" }}>
            {item.documentType === 1 && item.formNumber && item.latestVersion
              ? `${item.formNumber} ${item.latestVersion}`
              : item.latestVersion}
          </div>
          <div className="title" style={{ color: "#333" }}>
            {item.name}
          </div>
          <div className="title" style={{ color: "#333" }}>
            {formatPriceDisplay()}
          </div>
        </div>
      </div>

      {!hideCart && (
        <button
          className={`cart ${inCart ? "cart-active" : ""}`}
          onClick={handleCartClick}
          title={inCart ? "Remove from cart" : "Add to cart"}
          aria-label={inCart ? "Remove from cart" : "Add to cart"}
          type="button"
          disabled={loading}
          style={{ pointerEvents: "auto", position: "relative", zIndex: 10 }}
        >
          {renderCartIcon()}
        </button>
      )}
    </button>
  );
};

ContentCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    formNumber: PropTypes.string,
    latestVersion: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
    price: PropTypes.string.isRequired,
    documentType: PropTypes.number,
    typeOfContent: PropTypes.number,
    totalContent: PropTypes.number,
    contents: PropTypes.array,
    isAddedInCart: PropTypes.bool,
    isPreview: PropTypes.bool,
    previewType: PropTypes.number,
    marketingDescription: PropTypes.string,
    subscriptionOptions: PropTypes.array,
  }).isRequired,
  hideCart: PropTypes.bool,
};

export default ContentCard;
